<template>
  <!-- The MT morphemes behind one agreement count, with every column's words there. -->
  <div class="evidence">
    <p v-if="error" class="muted">{{ error }}</p>
    <ol>
      <li v-for="(m, i) in items" :key="i" class="morpheme">
        <div class="where">
          <span class="ref">{{ verseRef(m) }}</span>
          <span class="font-hebrew hebrew">{{ m.hebrew }}</span>
          <span class="parsing">{{ m.parsing.text }}</span>
        </div>
        <ul class="columns">
          <li v-for="(h, j) in m.renderings" :key="j" :class="{ 'is-unconfirmed': !h.confident }">
            <span class="col-name font-greek">{{ h.column }}</span>
            <router-link v-if="h.lemma_id" :to="`/lemma/${h.lemma_id}`" class="font-greek surface">{{ h.surface }}</router-link>
            <span v-else class="font-greek surface">{{ h.surface }}</span>
            <span v-if="h.lexeme" class="lexeme font-greek">{{ h.lexeme.headword }}</span>
          </li>
        </ul>
      </li>
    </ol>
    <button v-if="items.length < total" class="plain" :disabled="loading" @click="more">
      {{ loading ? 'Loading…' : `${total - items.length} more` }}
    </button>
    <p v-else-if="!loading && !items.length" class="muted">None.</p>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from 'vue'
import { evidence, Relation, Scope, SharedMorpheme } from '../api'
import { verseRef } from '../format'

export default defineComponent({
  name: 'EvidenceList',
  props: {
    a: { type: String, required: true },
    b: { type: String, required: true },
    relation: { type: String as PropType<Relation>, required: true },
    scope: { type: Object as PropType<Scope>, default: () => ({}) },
  },
  setup(props) {
    const items = ref<SharedMorpheme[]>([])
    const total = ref(0)
    const loading = ref(false)
    const error = ref('')
    let page = 0
    const more = async () => {
      loading.value = true
      try {
        const p = await evidence(props.a, props.b, props.relation, props.scope, page)
        items.value.push(...p.items)
        total.value = p.total
        page += 1
      } catch (e) {
        error.value = e instanceof Error ? e.message : 'Unavailable.'
      } finally {
        loading.value = false
      }
    }
    more()
    return { items, total, loading, error, more, verseRef }
  },
})
</script>

<style lang="scss" scoped>
@import '@/_colors';

.evidence {
  margin: 0.4rem 0 0.75rem 0.5rem;
  padding-left: 0.6rem;
  border-left: 2px solid $border;

  ol {
    list-style: none;
    margin: 0;
  }
}

.morpheme {
  padding: 0.35rem 0;
  border-bottom: 1px solid $border;
}

.where {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  font-size: 0.8rem;

  .ref {
    color: $text-medium;
  }

  .hebrew {
    font-size: 1.2rem;
    color: $text-dark;
  }

  .parsing {
    color: $text-light;
  }
}

.columns {
  list-style: none;
  margin: 0.15rem 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem 1rem;

  li {
    display: flex;
    align-items: baseline;
    gap: 0.35rem;
  }

  .is-unconfirmed .surface {
    text-decoration: underline dashed $text-light;
  }

  .col-name {
    color: $text-light;
    font-size: 0.8rem;
  }

  .surface {
    font-size: 1.05rem;
  }

  .lexeme {
    color: $text-light;
    font-size: 0.8rem;
  }
}

.plain {
  border: 0;
  background: none;
  cursor: pointer;
  color: $interactive;
  font-size: 0.8rem;
}

.muted {
  color: $text-light;
  font-size: 0.8rem;
}
</style>
